import { useEffect, useRef } from 'react';
import {
    Box,
    Divider,
    Link,
    List,
    ListItem,
    Typography,
} from "@mui/material";
import { ReactComponent as SadIcon } from "../../assets/icons/sad-face.svg"

const ResultsList = ({ title, items, highlightedIndex, baseIndex }) => {
    const itemRefs = useRef([]);

    useEffect(() => {
        const currentRef = itemRefs.current[highlightedIndex - baseIndex];
        if (currentRef && typeof currentRef.scrollIntoView === 'function') {
            currentRef.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [highlightedIndex, baseIndex]);

    return (
        <Box>
            <Typography color="primary" variant="body" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Divider sx={{ mt: ".5rem" }} />
            {items?.length ? (
                <List>
                    {items.map((item, idx) => {
                        const globalIndex = baseIndex + idx;
                        const isHighlighted = globalIndex === highlightedIndex;

                        return (
                            <ListItem
                                key={item.name}
                                ref={(el) => itemRefs.current[idx] = el}
                                sx={{
                                    padding: 0,
                                    borderRadius: 2,
                                    backgroundColor: isHighlighted ? "#e0f7fa" : "transparent",
                                    "&:hover": {
                                        backgroundColor: "#f5f5f5",
                                    }
                                }}
                            >
                                <Link
                                    href={item.path}
                                    sx={{
                                        py: ".5rem",
                                        px: 2,
                                        display: 'block',
                                        width: '100%',
                                        color: isHighlighted ? "#007B8F" : "#666B6E",
                                        fontWeight: isHighlighted ? 600 : 400
                                    }}
                                    underline="none"
                                >
                                    {item.name}
                                </Link>
                            </ListItem>
                        );
                    })}
                </List>
            ) : null}
        </Box>
    );
};

const SuggestionsList = ({ title, items, onSelect }) => (
    <Box>
        <Typography color="primary" variant="body" sx={{ fontWeight: 700 }}>
            {title}
        </Typography>
        <Divider sx={{ mt: ".5rem" }} />
        {items?.length ? (
            <List>
                <>
                    {
                        items.map(item => (
                            <ListItem
                                key={item.name}
                                sx={{
                                    padding: 0,
                                    "&:hover": {
                                        borderRadius: 2,
                                        backgroundColor: "#f5f5f5",
                                    },
                                    cursor: "pointer"
                                }}
                                onClick={() => onSelect(item.name)} // Trigger onSelect on click
                            >
                                <Link href={item.path} sx={{ py: ".5rem", color: "#666B6E" }} underline='none'>
                                    {item.name}
                                </Link>
                            </ListItem>
                        ))
                    }
                </>
            </List>
        ) : null}
    </Box>
)

const PredictionsList = ({ data, value, onSelect, highlightedIndex }) => {
    const { sugerencias, ...predictions } = data || {};
    const thereIsData = Object.keys(predictions).some((key) => data[key]?.length);
    const allItems = [];
    Object.keys(predictions).forEach((key) => {
        if (data[key]?.length) {
            data[key].forEach((item) => {
                allItems.push({ ...item, category: key });
            });
        }
    });

    if (!thereIsData) {
        return (
            <Box
                sx={{
                    p: "1.5rem",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: "1rem",
                        justifyContent: "center",
                        mb: "1.5rem",
                    }}
                >
                    <SadIcon />
                    <Typography
                        color="secondary"
                        variant="body"
                        sx={{ fontWeight: 700 }}
                    >
                        {`No hay resultados para "${value}"`}
                    </Typography>
                </Box>
                {data?.sugerencias?.length ? (
                    <SuggestionsList
                        title="Quizás quisiste decir..."
                        items={data?.sugerencias}
                        onSelect={onSelect} // Pasamos la función onSelect
                    />
                ) : null}
            </Box>
        );
    }
    return (
        <Box sx={{ p: "1rem", width: "100%" }}>
            {Object.entries(predictions).map(([key, items], sectionIdx) => {
                if (!items?.length) return null;

                return (
                    <ResultsList
                        key={key}
                        title={key}
                        items={items}
                        highlightedIndex={highlightedIndex}
                        baseIndex={Object.entries(predictions)
                            .slice(0, sectionIdx)
                            .reduce((acc, [_, items]) => acc + items.length, 0)}
                    />
                );
            })}
        </Box>
    );
};

export default PredictionsList
